// WHAT: Component displaying roadmap items breakdown by module with progress bars
// WHY:  Provides visual representation of item distribution across modules
// DOMAIN: Admin → Roadmap → Analytics
// DEPENDENCIES: Card, Badge, ProgressBar components, Lucide icons
// RISKS:
//   - NOTE: Division by zero if total is 0 - percentage calculation will be NaN
//   - NOTE: Module names are used as keys - ensure they are unique
//   - NOTE: Hover scale effect may cause layout shifts

import { LucideFolderOpen } from "lucide-react";
import React from "react";

import { Card, Badge, ProgressBar } from "@/components/ui";

/**
 * Props for ModuleBreakdown component
 */
interface ModuleBreakdownProperties {
  /** Object mapping module names to item counts */
  byModule: Record<string, number>;
  /** Total number of items for percentage calculation */
  total: number;
}

/**
 * WHAT:
 *   Component that displays a breakdown of roadmap items by module with
 *   progress bars showing the percentage distribution.
 *
 * WHY:
 *   Provides visual insight into how roadmap items are distributed across
 *   different modules, helping identify module workload distribution.
 *
 * PROPS:
 *   - byModule: Record<string, number> — Module name to item count mapping
 *   - total: number — Total item count for percentage calculation
 *
 * BEHAVIOR:
 *   - Renders module cards in responsive grid (1/2/3 columns)
 *   - Calculates percentage for each module (count / total * 100)
 *   - Displays progress bar and percentage text
 *   - Shows hover scale effect for interactivity
 *
 * RISKS:
 *   - NOTE: Division by zero if total is 0 - add validation or default value
 *   - NOTE: Percentage formatting to 1 decimal may lose precision
 *   - NOTE: Module names used as keys - ensure uniqueness
 *   - NOTE: Hover scale effect may cause layout shifts on smaller screens
 *
 * @param root0
 *
 * @param root0.byModule
 *
 * @param root0.total
 *
 * @returns {JSX.Element} Die gerenderte ModuleBreakdown-Komponente
 *
 */
export const ModuleBreakdown: React.FC<ModuleBreakdownProperties> = ({ byModule, total }) => {
  return (
    <Card padding="lg" variant="default">
      <div className="flex items-center gap-2 mb-6">
        <LucideFolderOpen className="w-5 h-5 text-orange-500" />
        <h3 className="text-lg font-medium text-white">Module Breakdown</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(byModule).map(([module, count]) => {
          const percentage = (count / total) * 100;

          return (
            <Card
              className="group hover:scale-[1.02] transition-all duration-200"
              key={module}
              padding="md"
              variant="outlined"
            >
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Badge className="truncate" size="sm" style="subtle" variant="default">
                    {module}
                  </Badge>
                  <span className="text-white font-semibold">{count}</span>
                </div>

                <ProgressBar
                  max={100}
                  showLabel={false}
                  size="sm"
                  state="default"
                  value={percentage}
                />

                <div className="text-xs text-slate-400">{percentage.toFixed(1)}% of total</div>
              </div>
            </Card>
          );
        })}
      </div>
    </Card>
  );
};
